// StockManagement.js
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiPackage, 
  FiSearch,
  FiX,
  FiCheck,
  FiEye,
  FiDollarSign,
  FiHash,
  FiGrid,
  FiList,
  FiAlertCircle,
  FiChevronDown,
  FiChevronUp,
  FiInfo,
  FiFilter,
  FiTrendingUp,
  FiClock,
  FiCalendar,
  FiTag,
  FiBox,
  FiCheckCircle,
  FiMinusCircle,
  FiPlusCircle,
  FiSave,
  FiRefreshCw,
  FiTrash2,
  FiEdit2,
  FiPlus,
  FiShoppingBag,
  FiGift,
  FiLayers,
  FiArchive
} from 'react-icons/fi';
import { stockAPI, productAPI, customProductItemAPI, accountAPI } from '../utils/api';
import toast from 'react-hot-toast';
import useResponsive from '../hooks/useResponsive';

const StockManagement = ({ initialProductId, initialCustomItemId }) => {
  const [stocks, setStocks] = useState([]);
  const [products, setProducts] = useState([]);
  const [customItems, setCustomItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stocksLoading, setStocksLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [stockToDelete, setStockToDelete] = useState(null);
  const [modalType, setModalType] = useState('create');
  const [selectedStock, setSelectedStock] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [usedFilter, setUsedFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [userRole, setUserRole] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [selectedProductId, setSelectedProductId] = useState(initialProductId || '');
  const [selectedCustomItemId, setSelectedCustomItemId] = useState(initialCustomItemId || '');
  const [viewMode, setViewMode] = useState('table');
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [showCustomItemDropdown, setShowCustomItemDropdown] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [customItemSearch, setCustomItemSearch] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [stockData, setStockData] = useState('');

  const [formData, setFormData] = useState({
    stock_data: ''
  });

  const { isMobile, isTablet, isDesktop } = useResponsive();

  // Color theme based on stock management (amber)
  const theme = {
    primary: 'from-amber-600 to-orange-600',
    primaryLight: 'from-amber-500 to-orange-500',
    secondary: 'from-amber-500 to-yellow-500',
    bg: 'bg-amber-100 dark:bg-amber-900/30',
    text: 'text-amber-600 dark:text-amber-400',
    border: 'border-amber-200 dark:border-amber-800',
    ring: 'ring-amber-500',
    hover: 'hover:bg-amber-50 dark:hover:bg-amber-900/20',
    gradient: 'from-amber-600 to-orange-600',
    lightBg: 'bg-amber-50 dark:bg-amber-900/20',
    badge: {
      success: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      warning: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
      error: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
      info: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      used: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
      unused: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
    }
  };

  useEffect(() => {
    fetchUserAndData();
  }, []);

  useEffect(() => {
    if (selectedCustomItemId) {
      fetchStocks(selectedCustomItemId);
    } else {
      setStocks([]);
    }
  }, [selectedCustomItemId]);

  const fetchUserAndData = async () => {
    try {
      setLoading(true);
      const userResponse = await accountAPI.getMe();
      if (userResponse.data.success) {
        const user = userResponse.data.data;
        setCurrentUser(user);
        setUserRole(user.role);
      }

      const productsResponse = await productAPI.list();
      if (productsResponse.data && productsResponse.data.success) {
        let allProducts = productsResponse.data.data || [];
        setProducts(allProducts);
      }

      if (selectedProductId) {
        await fetchCustomItems(selectedProductId);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomItems = async (productId) => {
    if (!productId) return;
    
    try {
      const response = await customProductItemAPI.list(productId);
      if (response.data && response.data.success) {
        setCustomItems(response.data.data || []);
      } else {
        setCustomItems([]);
      }
    } catch (error) {
      console.error('Error fetching custom items:', error);
      setCustomItems([]);
    }
  };

  const fetchStocks = async (customItemId) => {
    if (!customItemId) return;
    
    setStocksLoading(true);
    try {
      const includeUsed = usedFilter === 'all' ? 1 : (usedFilter === 'used' ? 1 : 0);
      const response = await stockAPI.list(customItemId, includeUsed);
      if (response.data && response.data.success) {
        setStocks(response.data.data || []);
      } else {
        setStocks([]);
      }
    } catch (error) {
      console.error('Error fetching stocks:', error);
      setStocks([]);
    } finally {
      setStocksLoading(false);
    }
  };

  const canCreate = () => ['system', 'super_admin'].includes(userRole);
  const canEdit = () => ['system', 'super_admin'].includes(userRole);
  const canDelete = () => ['system', 'super_admin'].includes(userRole);
  const canView = () => true;

  const getProductDetails = (productId) => {
    return products.find(p => p.id === parseInt(productId));
  };

  const getCustomItemDetails = (itemId) => {
    return customItems.find(i => i.id === parseInt(itemId));
  };

  const validateForm = () => {
    const errors = {};

    if (!stockData.trim()) {
      errors.stock_data = 'Stock data is required';
    } else {
      const lines = stockData.trim().split('\n');
      if (lines.length === 0) {
        errors.stock_data = 'At least one line of stock data is required';
      }
    }

    if (!selectedCustomItemId) {
      errors.custom_item = 'Please select a custom item';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreate = async () => {
    if (!canCreate()) {
      toast.error('You do not have permission to create stock');
      return;
    }

    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await stockAPI.create({
        custom_item_id: parseInt(selectedCustomItemId),
        stock_data: stockData
      });
      
      if (response.data && response.data.success) {
        toast.success('Stock created successfully');
        setShowModal(false);
        setStockData('');
        fetchStocks(selectedCustomItemId);
      } else {
        toast.error(response.data?.message || 'Failed to create stock');
      }
    } catch (error) {
      console.error('Error creating stock:', error);
      toast.error(error.response?.data?.message || 'Failed to create stock');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdate = async () => {
    if (!canEdit()) {
      toast.error('You do not have permission to update stock');
      return;
    }

    if (!selectedStock) {
      toast.error('No stock selected');
      return;
    }

    if (!stockData.trim()) {
      toast.error('Stock data is required');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await stockAPI.update(selectedStock.id, {
        stock_data: stockData,
        is_used: selectedStock.is_used
      });
      
      if (response.data && response.data.success) {
        toast.success('Stock updated successfully');
        setShowModal(false);
        setStockData('');
        setSelectedStock(null);
        fetchStocks(selectedCustomItemId);
      } else {
        toast.error(response.data?.message || 'Failed to update stock');
      }
    } catch (error) {
      console.error('Error updating stock:', error);
      toast.error(error.response?.data?.message || 'Failed to update stock');
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleUsed = async (stock) => {
    if (!canEdit()) {
      toast.error('You do not have permission to update stock');
      return;
    }

    try {
      const response = await stockAPI.update(stock.id, {
        is_used: stock.is_used === 1 ? 0 : 1
      });
      
      if (response.data && response.data.success) {
        toast.success(`Stock marked as ${stock.is_used === 1 ? 'unused' : 'used'}`);
        fetchStocks(selectedCustomItemId);
      } else {
        toast.error(response.data?.message || 'Failed to update stock');
      }
    } catch (error) {
      console.error('Error updating stock:', error);
      toast.error(error.response?.data?.message || 'Failed to update stock');
    }
  };

  const confirmDelete = (stock) => {
    setStockToDelete(stock);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!canDelete() || !stockToDelete) return;

    try {
      const response = await stockAPI.delete(stockToDelete.id);
      if (response.data && response.data.success) {
        toast.success('Stock deleted successfully');
        fetchStocks(selectedCustomItemId);
      } else {
        toast.error(response.data?.message || 'Failed to delete stock');
      }
    } catch (error) {
      console.error('Error deleting stock:', error);
      toast.error(error.response?.data?.message || 'Failed to delete stock');
    } finally {
      setShowDeleteModal(false);
      setStockToDelete(null);
    }
  };

  const openEditModal = (stock) => {
    if (!canEdit()) {
      toast.error('You do not have permission to edit stock');
      return;
    }

    setSelectedStock(stock);
    setStockData(stock.stock_data);
    setModalType('edit');
    setShowModal(true);
    setFormErrors({});
  };

  const openCreateModal = () => {
    if (!canCreate()) {
      toast.error('You do not have permission to create stock');
      return;
    }

    if (!selectedCustomItemId) {
      toast.error('Please select a custom item first');
      return;
    }

    setStockData('');
    setSelectedStock(null);
    setModalType('create');
    setShowModal(true);
    setFormErrors({});
  };

  const handleProductChange = (productId) => {
  if (productId && !isProductStockBased(productId)) {
    toast.error('Selected product is not stock-based');
    return;
  }
  
  setSelectedProductId(productId);
  setSelectedCustomItemId('');
  setCustomItems([]);
  setStocks([]);
  if (productId) {
    fetchCustomItems(productId);
  }
};

  const handleCustomItemChange = (itemId) => {
    setSelectedCustomItemId(itemId);
    if (!itemId) {
      setStocks([]);
    }
  };

  // Update the product dropdown to only show stock-based products
const filteredProducts = products.filter(product =>
  product.is_stock_based === 1 && // Only show stock-based products
  (product.name.toLowerCase().includes(productSearch.toLowerCase()) ||
   product.code.toLowerCase().includes(productSearch.toLowerCase()))
);

// Update the custom item filtering
const filteredCustomItems = customItems.filter(item =>
  item.name.toLowerCase().includes(customItemSearch.toLowerCase()) ||
  item.code.toLowerCase().includes(customItemSearch.toLowerCase())
);

const isProductStockBased = (productId) => {
  const product = products.find(p => p.id === parseInt(productId));
  return product?.is_stock_based === 1;
};

  const selectedProduct = getProductDetails(selectedProductId);
  const selectedCustomItem = getCustomItemDetails(selectedCustomItemId);

  const getSortedStocks = () => {
    let filtered = stocks.filter(stock => {
      const matchesSearch = stock.stock_data.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesUsed = usedFilter === 'all' || 
                         (usedFilter === 'used' && stock.is_used === 1) ||
                         (usedFilter === 'unused' && stock.is_used === 0);
      
      return matchesSearch && matchesUsed;
    });

    switch(sortBy) {
      case 'newest':
        return filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      case 'oldest':
        return filtered.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      case 'used':
        return filtered.sort((a, b) => b.is_used - a.is_used);
      case 'unused':
        return filtered.sort((a, b) => a.is_used - b.is_used);
      default:
        return filtered;
    }
  };

  const displayedStocks = getSortedStocks();

  const totalStocks = displayedStocks.length;
  const usedStocks = displayedStocks.filter(s => s.is_used === 1).length;
  const unusedStocks = displayedStocks.filter(s => s.is_used === 0).length;

  const StockGridCard = ({ stock }) => {
    const canEditThis = canEdit();
    
    return (
      <motion.div
        layout
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        whileHover={{ y: -4 }}
        className="group bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 dark:border-gray-700"
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between mb-4">
            <div className={`w-10 h-10 rounded-xl ${theme.bg} flex items-center justify-center`}>
              <FiArchive className={`w-5 h-5 ${theme.text}`} />
            </div>
            <span className="text-xs text-gray-400">ID: {stock.id}</span>
          </div>

          <div className="mb-3">
            <span className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
              stock.is_used === 1 ? theme.badge.used : theme.badge.unused
            }`}>
              {stock.is_used === 1 ? 'Used' : 'Unused'}
            </span>
          </div>

          <p className="text-sm text-gray-900 dark:text-white font-mono bg-gray-50 dark:bg-gray-700 p-3 rounded-lg mb-3 break-all">
            {stock.stock_data}
          </p>

          <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700 flex justify-between items-center">
            <div className="flex items-center gap-1 text-xs text-gray-400">
              <FiCalendar className="w-3 h-3" />
              {new Date(stock.created_at).toLocaleDateString()}
            </div>
            {canEditThis && (
              <div className="flex gap-1">
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => toggleUsed(stock)}
                  className={`p-2 ${
                    stock.is_used === 1 
                      ? 'text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/30' 
                      : 'text-green-600 hover:bg-green-50 dark:hover:bg-green-900/30'
                  } rounded-xl transition-colors`}
                  title={stock.is_used === 1 ? 'Mark as unused' : 'Mark as used'}
                >
                  {stock.is_used === 1 ? <FiMinusCircle className="w-4 h-4" /> : <FiCheckCircle className="w-4 h-4" />}
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => openEditModal(stock)}
                  className={`p-2 ${theme.hover} rounded-xl transition-colors ${theme.text}`}
                  title="Edit stock"
                >
                  <FiEdit2 className="w-4 h-4" />
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => confirmDelete(stock)}
                  className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-xl transition-colors"
                  title="Delete stock"
                >
                  <FiTrash2 className="w-4 h-4" />
                </motion.button>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    );
  };

  const StockTableRow = ({ stock }) => {
    const canEditThis = canEdit();
    
    return (
      <motion.tr
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
      >
        <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">
          {stock.id}
        </td>
        <td className="px-4 sm:px-6 py-4 text-sm text-gray-900 dark:text-gray-200 max-w-md">
          <div className="truncate font-mono">{stock.stock_data}</div>
        </td>
        <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
          <span className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
            stock.is_used === 1 ? theme.badge.used : theme.badge.unused
          }`}>
            {stock.is_used === 1 ? 'Used' : 'Unused'}
          </span>
        </td>
        <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
          <div className="flex items-center gap-1">
            <FiCalendar className="w-3 h-3" />
            {new Date(stock.created_at).toLocaleDateString()}
          </div>
        </td>
        <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm">
          {canEditThis && (
            <div className="flex items-center gap-2">
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => toggleUsed(stock)}
                className={`p-2 ${
                  stock.is_used === 1 
                    ? 'text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/30' 
                    : 'text-green-600 hover:bg-green-50 dark:hover:bg-green-900/30'
                } rounded-lg transition-colors`}
                title={stock.is_used === 1 ? 'Mark as unused' : 'Mark as used'}
              >
                {stock.is_used === 1 ? <FiMinusCircle className="w-4 h-4" /> : <FiCheckCircle className="w-4 h-4" />}
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => openEditModal(stock)}
                className={`p-2 ${theme.hover} rounded-lg transition-colors ${theme.text}`}
                title="Edit"
              >
                <FiEdit2 className="w-4 h-4" />
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => confirmDelete(stock)}
                className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                title="Delete"
              >
                <FiTrash2 className="w-4 h-4" />
              </motion.button>
            </div>
          )}
        </td>
      </motion.tr>
    );
  };

  return (
    <div className="space-y-6 pb-20 md:pb-6">
      {/* Header Section */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className={`bg-gradient-to-r ${theme.gradient} rounded-2xl p-6 sm:p-8 shadow-lg`}
      >
        <div className="flex items-center gap-4">
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center"
          >
            <FiArchive className="w-7 h-7 text-white" />
          </motion.div>
          <div>
            <motion.h1
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="text-2xl md:text-3xl font-bold text-white"
            >
              Stock Management
            </motion.h1>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="text-amber-100 text-sm mt-1"
            >
              Manage stock for custom items
            </motion.p>
          </div>
        </div>
      </motion.div>

      {/* Selection Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Product Selection */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-100 dark:border-gray-700"
        >
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            Select Product <span className="text-red-500">*</span>
          </label>
          
          <div className="relative">
            <button
              onClick={() => setShowProductDropdown(!showProductDropdown)}
              className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent text-left flex items-center justify-between text-gray-900 dark:text-white hover:border-amber-300 transition-colors"
            >
              <span className="truncate flex items-center gap-2">
                {selectedProduct ? (
                  <>
                    <FiShoppingBag className={`w-4 h-4 ${theme.text}`} />
                    <span className="font-medium">{selectedProduct.name}</span>
                    <span className="text-gray-400">({selectedProduct.code})</span>
                  </>
                ) : (
                  <span className="text-gray-400">-- Choose a product --</span>
                )}
              </span>
              <motion.div
                animate={{ rotate: showProductDropdown ? 180 : 0 }}
                transition={{ duration: 0.2 }}
              >
                <FiChevronDown className="w-5 h-5 text-gray-400" />
              </motion.div>
            </button>

            <AnimatePresence>
              {showProductDropdown && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="absolute z-10 mt-2 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg overflow-hidden"
                >
                  <div className="p-3 border-b border-gray-100 dark:border-gray-700">
                    <div className="relative">
                      <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search products..."
                        value={productSearch}
                        onChange={(e) => setProductSearch(e.target.value)}
                        className="w-full pl-9 pr-4 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm text-gray-900 dark:text-white placeholder-gray-400"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                  </div>

                  <div className="max-h-60 overflow-y-auto scrollbar-hide">
                    {filteredProducts.length === 0 ? (
                      <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">
                        No products found
                      </div>
                    ) : (
                      filteredProducts.map((product) => (
                        <motion.button
                          key={product.id}
                          whileHover={{ backgroundColor: 'rgba(245, 158, 11, 0.05)' }}
                          onClick={() => {
                            handleProductChange(String(product.id));
                            setShowProductDropdown(false);
                            setProductSearch('');
                          }}
                          className="w-full px-4 py-3 text-left flex items-center justify-between border-b border-gray-100 dark:border-gray-700 last:border-0"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="w-8 h-8 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                              <FiShoppingBag className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-sm text-gray-900 dark:text-white truncate">
                                {product.name}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                Code: {product.code}
                              </p>
                            </div>
                          </div>
                          {selectedProductId === String(product.id) && (
                            <FiCheckCircle className={`w-5 h-5 ${theme.text}`} />
                          )}
                        </motion.button>
                      ))
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>

        {/* Custom Item Selection */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-100 dark:border-gray-700"
        >
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            Select Custom Item <span className="text-red-500">*</span>
          </label>
          
          <div className="relative">
            <button
              onClick={() => setShowCustomItemDropdown(!showCustomItemDropdown)}
              disabled={!selectedProductId}
              className={`w-full px-4 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent text-left flex items-center justify-between text-gray-900 dark:text-white hover:border-amber-300 transition-colors ${
                !selectedProductId ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              <span className="truncate flex items-center gap-2">
                {selectedCustomItem ? (
                  <>
                    <FiGift className={`w-4 h-4 ${theme.text}`} />
                    <span className="font-medium">{selectedCustomItem.name}</span>
                    <span className="text-gray-400">({selectedCustomItem.code})</span>
                  </>
                ) : (
                  <span className="text-gray-400">
                    {selectedProductId ? '-- Choose a custom item --' : '-- Select a product first --'}
                  </span>
                )}
              </span>
              <motion.div
                animate={{ rotate: showCustomItemDropdown ? 180 : 0 }}
                transition={{ duration: 0.2 }}
              >
                <FiChevronDown className="w-5 h-5 text-gray-400" />
              </motion.div>
            </button>

            <AnimatePresence>
              {showCustomItemDropdown && selectedProductId && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="absolute z-10 mt-2 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg overflow-hidden"
                >
                  <div className="p-3 border-b border-gray-100 dark:border-gray-700">
                    <div className="relative">
                      <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search custom items..."
                        value={customItemSearch}
                        onChange={(e) => setCustomItemSearch(e.target.value)}
                        className="w-full pl-9 pr-4 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm text-gray-900 dark:text-white placeholder-gray-400"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                  </div>

                  <div className="max-h-60 overflow-y-auto scrollbar-hide">
                    {filteredCustomItems.length === 0 ? (
                      <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">
                        No custom items found
                      </div>
                    ) : (
                      filteredCustomItems.map((item) => (
                        <motion.button
                          key={item.id}
                          whileHover={{ backgroundColor: 'rgba(245, 158, 11, 0.05)' }}
                          onClick={() => {
                            handleCustomItemChange(String(item.id));
                            setShowCustomItemDropdown(false);
                            setCustomItemSearch('');
                          }}
                          className="w-full px-4 py-3 text-left flex items-center justify-between border-b border-gray-100 dark:border-gray-700 last:border-0"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="w-8 h-8 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                              <FiGift className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-sm text-gray-900 dark:text-white truncate">
                                {item.name}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                Code: {item.code}
                              </p>
                            </div>
                          </div>
                          {selectedCustomItemId === String(item.id) && (
                            <FiCheckCircle className={`w-5 h-5 ${theme.text}`} />
                          )}
                        </motion.button>
                      ))
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </div>

      {selectedCustomItemId && (
        <>
          {/* Statistics Cards */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="grid grid-cols-2 lg:grid-cols-4 gap-4"
          >
            {[
              { label: 'Total Stock', value: totalStocks, icon: FiArchive, color: 'amber' },
              { label: 'Used', value: usedStocks, icon: FiCheckCircle, color: 'purple' },
              { label: 'Unused', value: unusedStocks, icon: FiClock, color: 'green' },
              { label: 'Usage Rate', value: totalStocks > 0 ? `${Math.round((usedStocks / totalStocks) * 100)}%` : '0%', icon: FiTrendingUp, color: 'blue' }
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 + index * 0.1 }}
                className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-md transition-all"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{stat.label}</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{stat.value}</p>
                  </div>
                  <div className={`w-10 h-10 bg-${stat.color}-100 dark:bg-${stat.color}-900/30 rounded-lg flex items-center justify-center`}>
                    <stat.icon className={`w-5 h-5 text-${stat.color}-600 dark:text-${stat.color}-400`} />
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Search and Actions Bar */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="flex flex-col gap-4"
          >
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search stock data..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent text-gray-900 dark:text-white placeholder-gray-400"
                />
              </div>

              <div className="flex gap-2">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-4 py-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm text-gray-900 dark:text-white"
                >
                  <option value="newest">Newest</option>
                  <option value="oldest">Oldest</option>
                  <option value="used">Used first</option>
                  <option value="unused">Unused first</option>
                </select>

                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`px-4 py-3 border rounded-xl flex items-center gap-2 transition-colors ${
                    showFilters 
                      ? 'bg-amber-600 text-white border-amber-600' 
                      : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-amber-300'
                  }`}
                >
                  <FiFilter className="w-5 h-5" />
                  <span className="hidden sm:inline">Filter</span>
                </button>

                {!isMobile && (
                  <div className="hidden sm:flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                    <button
                      onClick={() => setViewMode('table')}
                      className={`p-2 rounded-lg transition-colors ${
                        viewMode === 'table' 
                          ? 'bg-white dark:bg-gray-600 text-amber-600 shadow-sm' 
                          : 'text-gray-500 hover:text-gray-700 dark:text-gray-400'
                      }`}
                      title="Table view"
                    >
                      <FiList className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => setViewMode('grid')}
                      className={`p-2 rounded-lg transition-colors ${
                        viewMode === 'grid' 
                          ? 'bg-white dark:bg-gray-600 text-amber-600 shadow-sm' 
                          : 'text-gray-500 hover:text-gray-700 dark:text-gray-400'
                      }`}
                      title="Grid view"
                    >
                      <FiGrid className="w-5 h-5" />
                    </button>
                  </div>
                )}

                {canCreate() && (
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={openCreateModal}
                    className={`px-4 py-3 bg-gradient-to-r ${theme.primary} text-white rounded-xl hover:from-amber-700 hover:to-orange-700 transition-all shadow-lg shadow-amber-500/20 flex items-center gap-2 whitespace-nowrap`}
                  >
                    <FiPlus className="w-5 h-5" />
                    <span className="hidden sm:inline">Add Stock</span>
                  </motion.button>
                )}
              </div>
            </div>

            <AnimatePresence>
              {showFilters && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-100 dark:border-gray-700"
                >
                  <div className="flex flex-wrap items-center gap-4">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Status:</span>
                    <div className="flex gap-2">
                      {[
                        { value: 'all', label: 'All' },
                        { value: 'used', label: 'Used' },
                        { value: 'unused', label: 'Unused' }
                      ].map((status) => (
                        <motion.button
                          key={status.value}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => setUsedFilter(status.value)}
                          className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                            usedFilter === status.value
                              ? status.value === 'used' 
                                ? 'bg-purple-600 text-white'
                                : status.value === 'unused'
                                ? 'bg-green-600 text-white'
                                : 'bg-amber-600 text-white'
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                          }`}
                        >
                          {status.label}
                        </motion.button>
                      ))}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          {/* Stocks Display */}
          {stocksLoading ? (
            <div className="flex justify-center items-center h-64">
              <div className="relative">
                <div className="w-16 h-16 border-4 border-amber-200 dark:border-amber-900 rounded-full"></div>
                <div className="w-16 h-16 border-4 border-amber-600 border-t-transparent rounded-full animate-spin absolute top-0 left-0"></div>
              </div>
            </div>
          ) : displayedStocks.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white dark:bg-gray-800 rounded-2xl p-12 text-center border border-gray-100 dark:border-gray-700"
            >
              <div className="w-24 h-24 mx-auto bg-gradient-to-br from-amber-100 to-orange-100 dark:from-amber-900/30 dark:to-orange-900/30 rounded-full flex items-center justify-center mb-4">
                <FiArchive className="w-12 h-12 text-amber-600 dark:text-amber-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Stock Found</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                {searchTerm || usedFilter !== 'all' || sortBy !== 'newest'
                  ? 'No stock items match your filters.' 
                  : 'Get started by adding your first stock item.'}
              </p>
              {canCreate() && !searchTerm && usedFilter === 'all' && sortBy === 'newest' && (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={openCreateModal}
                  className={`inline-flex items-center px-4 py-2 bg-gradient-to-r ${theme.primary} text-white rounded-lg hover:from-amber-700 hover:to-orange-700 transition-colors text-sm shadow-lg shadow-amber-500/20`}
                >
                  <FiPlus className="mr-2 w-4 h-4" />
                  Add your first stock
                </motion.button>
              )}
            </motion.div>
          ) : (
            <>
              {isMobile ? (
                <div className="grid grid-cols-1 gap-4">
                  <AnimatePresence>
                    {displayedStocks.map((stock) => (
                      <StockGridCard key={stock.id} stock={stock} />
                    ))}
                  </AnimatePresence>
                </div>
              ) : (
                <>
                  {viewMode === 'grid' ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <AnimatePresence>
                        {displayedStocks.map((stock) => (
                          <StockGridCard key={stock.id} stock={stock} />
                        ))}
                      </AnimatePresence>
                    </div>
                  ) : (
                    <div className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-sm border border-gray-100 dark:border-gray-700">
                      <div className="overflow-x-auto scrollbar-hide">
                        <table className="w-full min-w-[800px]">
                          <thead className="bg-gray-50 dark:bg-gray-700">
                            <tr>
                              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">ID</th>
                              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Stock Data</th>
                              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Created</th>
                              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                            <AnimatePresence>
                              {displayedStocks.map((stock) => (
                                <StockTableRow key={stock.id} stock={stock} />
                              ))}
                            </AnimatePresence>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </>
      )}

      {/* Create/Edit Modal */}
      <AnimatePresence>
        {showModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowModal(false)}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
            />
            
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
                <div className={`bg-gradient-to-r ${theme.gradient} px-6 py-5`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <motion.div
                        initial={{ scale: 0, rotate: -180 }}
                        animate={{ scale: 1, rotate: 0 }}
                        transition={{ type: "spring", stiffness: 200 }}
                        className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center"
                      >
                        {modalType === 'create' ? (
                          <FiPlus className="w-5 h-5 text-white" />
                        ) : (
                          <FiEdit2 className="w-5 h-5 text-white" />
                        )}
                      </motion.div>
                      <div>
                        <h3 className="text-lg font-semibold text-white">
                          {modalType === 'create' ? 'Add Stock' : 'Edit Stock'}
                        </h3>
                        {selectedCustomItem && (
                          <p className="text-sm text-amber-100">
                            Item: {selectedCustomItem.name} ({selectedCustomItem.code})
                          </p>
                        )}
                      </div>
                    </div>
                    <motion.button
                      whileHover={{ scale: 1.1, rotate: 90 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => setShowModal(false)}
                      className="p-2 hover:bg-white/20 rounded-xl transition-colors"
                    >
                      <FiX className="w-5 h-5 text-white" />
                    </motion.button>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto scrollbar-hide p-6 bg-gray-50 dark:bg-gray-900">
                  <div className="space-y-5">
                    {/* Stock Data Field */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Stock Data <span className="text-red-500">*</span>
                      </label>
                      {modalType === 'create' ? (
                        <textarea
                          value={stockData}
                          onChange={(e) => setStockData(e.target.value)}
                          rows="8"
                          className={`
                            w-full px-4 py-3 bg-white dark:bg-gray-800 
                            border-2 rounded-xl text-sm text-gray-900 dark:text-white font-mono
                            focus:ring-2 focus:ring-amber-500 focus:border-transparent
                            transition-all duration-200
                            ${formErrors.stock_data 
                              ? 'border-red-300 focus:ring-red-500' 
                              : 'border-gray-200 dark:border-gray-700 hover:border-amber-300'
                            }
                          `}
                          placeholder="Enter stock data (one item per line)"
                        />
                      ) : (
                        <input
                          type="text"
                          value={stockData}
                          onChange={(e) => setStockData(e.target.value)}
                          className={`
                            w-full px-4 py-3 bg-white dark:bg-gray-800 
                            border-2 rounded-xl text-sm text-gray-900 dark:text-white font-mono
                            focus:ring-2 focus:ring-amber-500 focus:border-transparent
                            transition-all duration-200
                            ${formErrors.stock_data 
                              ? 'border-red-300 focus:ring-red-500' 
                              : 'border-gray-200 dark:border-gray-700 hover:border-amber-300'
                            }
                          `}
                          placeholder="Enter stock data"
                        />
                      )}
                      {formErrors.stock_data && (
                        <motion.p
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="mt-2 text-xs text-red-500 flex items-center gap-1"
                        >
                          <FiAlertCircle className="w-3 h-3" />
                          {formErrors.stock_data}
                        </motion.p>
                      )}
                      <p className="mt-2 text-xs text-gray-500">
                        {modalType === 'create'
                          ? 'Enter one stock item per line. Each line will be saved as a separate stock entry.'
                          : 'Edit mode supports a single stock value, so this field is kept to one line.'}
                      </p>
                    </div>

                    {modalType === 'edit' && selectedStock && (
                      <div className="p-4 bg-gray-100 dark:bg-gray-700 rounded-xl">
                        <p className="text-xs text-gray-600 dark:text-gray-400">
                          Current status: <span className={`font-medium ${selectedStock.is_used === 1 ? 'text-purple-600' : 'text-green-600'}`}>
                            {selectedStock.is_used === 1 ? 'Used' : 'Unused'}
                          </span>
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="border-t-2 border-gray-100 dark:border-gray-700 px-6 py-4 bg-white dark:bg-gray-800">
                  <div className="flex justify-end gap-3">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setShowModal(false)}
                      className="px-6 py-3 rounded-xl font-medium text-sm border-2 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all"
                      disabled={isSubmitting}
                    >
                      Cancel
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={modalType === 'create' ? handleCreate : handleUpdate}
                      disabled={isSubmitting}
                      className={`px-8 py-3 bg-gradient-to-r ${theme.primary} text-white rounded-xl font-medium text-sm hover:from-amber-700 hover:to-orange-700 transition-all shadow-lg shadow-amber-500/20 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {isSubmitting ? (
                        <>
                          <FiRefreshCw className="w-4 h-4 animate-spin" />
                          <span>Processing...</span>
                        </>
                      ) : (
                        <>
                          <FiSave className="w-4 h-4" />
                          <span>{modalType === 'create' ? 'Add Stock' : 'Update Stock'}</span>
                        </>
                      )}
                    </motion.button>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {showDeleteModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowDeleteModal(false)}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
            />
            
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
            >
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md">
                <div className="p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                      <FiAlertCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Delete Stock</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Are you sure you want to delete this stock item?
                      </p>
                    </div>
                  </div>
                  
                  {stockToDelete && (
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 mb-6">
                      <p className="text-sm text-gray-900 dark:text-white font-mono break-all">
                        {stockToDelete.stock_data}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                        Status: {stockToDelete.is_used === 1 ? 'Used' : 'Unused'}
                      </p>
                    </div>
                  )}
                  
                  <div className="flex justify-end gap-3">
                    <button
                      onClick={() => setShowDeleteModal(false)}
                      className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleDelete}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
                    >
                      <FiTrash2 className="w-4 h-4" />
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
};

export default StockManagement;
